import { useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPerson,
    faPeopleGroup,
    faUser,
    faCalendar,
    faUserMd,
    faTag,
} from "@fortawesome/free-solid-svg-icons";

import PaginatorComponent from "../../../general/auxiliar/ui/PaginatorComponent";
import EmptyTable from "../../../general/auxiliar/table/EmptyTable";
import LoadingTable from "../../../general/auxiliar/table/LoadingTable";
import OverviewTable from "../../../general/auxiliar/table/OverviewTable";
import { OverviewCardList } from "../../../general/auxiliar/table/OverviewCard";
import usePaginationFilterEffects from "../../../../auxiliar/customHooks/usePaginationFilterEffects";
import { getAllSessionsInVoucher } from "../../../../actions/voucher";
import { getTableState } from "../../../../auxiliar/auxFunctions";

const SessionCardList = ({ sessions }) => {
    const renderTitle = (session) => session.patient_name;

    const renderSubtitle = (session) => (
        <div className="space-y-1">
            <div className="flex items-center">
                <FontAwesomeIcon icon={faCalendar} className="mr-2 text-primary w-3 h-3" />
                <span className="text-xs sm:text-sm">{session.session_date}</span>
            </div>
            <div className="flex items-center">
                <FontAwesomeIcon icon={faUserMd} className="mr-2 text-primary w-3 h-3" />
                <span className="text-xs sm:text-sm truncate">{session.employee_name}</span>
            </div>
        </div>
    );

    const renderFooter = (session) => (
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                <FontAwesomeIcon
                    icon={faTag}
                    className="text-base-content/40 w-3 h-3 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm text-base-content/70 truncate">
                    {session.session_type}
                </span>
            </div>
            {renderSessionTypeIcon(session.type)}
        </div>
    );

    return (
        <OverviewCardList
            data={sessions}
            renderTitle={renderTitle}
            renderSubtitle={renderSubtitle}
            renderFooter={renderFooter}
            renderActions={() => null}
        />
    );
};

const SessionTable = ({ sessions }) => {
    const columns = [
        {
            label: "Fecha",
            icon: faCalendar,
            width: "w-[13%]",
        },
        {
            label: "Paciente",
            icon: faUser,
            width: "w-[29%]",
        },
        {
            label: "Fisioterapeuta",
            icon: faUserMd,
            width: "w-[29%]",
            cellClass: "hidden md:table-cell",
        },
        {
            label: "Tipo",
            icon: faTag,
            width: "w-[29%]",
            cellClass: "hidden sm:table-cell",
        },
    ];

    const getRowData = (session) => [
        <span key={`${session.session_id}-date`} className="font-semibold text-base-content">
            {session.session_date}
        </span>,
        <div
            key={`${session.session_id}-patient`}
            className="text-base-content/70 truncate"
            title={session.patient_name}
        >
            {session.patient_name}
        </div>,
        <span
            key={`${session.session_id}-employee`}
            className="text-base-content/70 truncate"
            title={session.employee_name}
        >
            {session.employee_name}
        </span>,
        <span key={`${session.session_id}-type`} className="text-base-content/70 truncate">
            {renderSessionTypeIcon(session.type)}
            {session.session_type}
        </span>,
    ];

    return (
        <OverviewTable
            data={sessions}
            columns={columns}
            getRowData={getRowData}
            actionsWidth="w-[10%]"
        />
    );
};

const renderSessionTypeIcon = (type) => {
    return type === "group_session" ? (
        <FontAwesomeIcon
            icon={faPeopleGroup}
            alt="Sesión grupal"
            className="w-4 h-4 mr-2"
            title="Sesión grupal"
        />
    ) : (
        <FontAwesomeIcon
            icon={faPerson}
            alt="Sesión individual"
            className="w-4 h-4 mr-2"
            title="Sesión individual"
        />
    );
};

const DisplaySessionsInAcquiredVoucher = ({ acquiredVoucherId, refreshBool }) => {
    const { id: activeClinicId } = useSelector((state) => state.activeClinic);
    const [sessions, setSessions] = useState({ waiting: true });
    const [filter, setFilter] = useState({
        clinic_id: activeClinicId,
        acquired_voucher_id: acquiredVoucherId,
        order: "date",
        per_page: 5,
        page: 1,
    });

    const disabled = usePaginationFilterEffects(
        filter,
        undefined,
        getAllSessionsInVoucher,
        setSessions,
        setFilter,
        refreshBool,
    );

    const tableState = getTableState(sessions);

    const renderContent = () => {
        switch (tableState) {
            case "loading":
                return <LoadingTable message="Cargando sesiones..." />;

            case "empty":
                return (
                    <EmptyTable
                        icon={faCalendar}
                        title="No hay sesiones registradas"
                        description="No hay sesiones pagadas con este bono."
                    />
                );

            case "hasData":
            default:
                return (
                    <>
                        <SessionCardList sessions={sessions} />
                        <SessionTable sessions={sessions} />
                    </>
                );
        }
    };

    return (
        <div className="my-3 mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between mb-4">
                <h3 className="text-primary text-2xl">Sesiones Asociadas</h3>

                <div className="w-full sm:w-auto">
                    <PaginatorComponent
                        pagination={sessions.pagination}
                        setFilter={setFilter}
                        filter={filter}
                        disabled={disabled}
                    />
                </div>
            </div>

            {renderContent()}

            {/* Pagination for small screens - duplicated to improve UX */}
            <div className="w-full my-5 sm:w-auto lg:hidden">
                <PaginatorComponent
                    pagination={sessions.pagination}
                    setFilter={setFilter}
                    filter={filter}
                    disabled={disabled}
                    showTotalItems={false}
                />
            </div>
        </div>
    );
};

export default DisplaySessionsInAcquiredVoucher;
